import { useState, useCallback } from 'react';

import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import { useTheme } from '@mui/material/styles';

import { useAuthContext } from 'src/auth/hooks';
import { NavList } from './nav-list';
import { navSectionClasses } from '../classes';
import { navSectionCssVars } from '../css-vars';
import { NavUl, NavLi, Subheader } from '../styles';

// ----------------------------------------------------------------------

export function NavSectionVertical({
  sx,
  data,
  render,
  slotProps,
  enabledRootRedirect,
  cssVars: overridesVars,
}) {
  const theme = useTheme();

  const { user } = useAuthContext();

  const allowed = (userTypes) => {
    if (!userTypes) return true;
    const userRoles = user?.role?.map((role) => role.name) || [];

    return userTypes.some((type) => userRoles.includes(type));
  };

  const cssVars = {
    ...navSectionCssVars.vertical(theme),
    ...overridesVars,
  };

  return (
    <Stack component="nav" className={navSectionClasses.vertical.root} sx={{ ...cssVars, ...sx }}>
      <NavUl sx={{ flex: '1 1 auto', gap: 'var(--nav-item-gap)' }}>
        {data.map((group) => {
          const { userTypes } = group;

          const allowedUserType = allowed(userTypes);

          // If it's a single nav item (no items array), render it directly
          if (!group?.items && group?.title) {
            return allowedUserType ? (
              <NavList
                key={group.title}
                data={group}
                render={render}
                depth={1}
                slotProps={slotProps}
                enabledRootRedirect={enabledRootRedirect}
              />
            ) : null;
          }

          // Otherwise, render as a group
          return (
            <Group
              key={group?.subheader ?? group?.items?.[0]?.title ?? group?.title}
              subheader={allowedUserType && group?.subheader}
              items={group?.items}
              render={render}
              slotProps={slotProps}
              enabledRootRedirect={enabledRootRedirect}
              isOpen={group?.isOpen}
            />
          );
        })}
      </NavUl>
    </Stack>
  );
}

// ----------------------------------------------------------------------

function Group({ items, render, subheader, slotProps, enabledRootRedirect, isOpen }) {
  const [open, setOpen] = useState(isOpen || false);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const renderContent = items ? (
    <NavUl sx={{ gap: 'var(--nav-item-gap)' }}>
      {items.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={1}
          slotProps={slotProps}
          enabledRootRedirect={enabledRootRedirect}
        />
      ))}
    </NavUl>
  ) : null;

  return (
    <NavLi>
      {subheader ? (
        <>
          <Subheader
            data-title={subheader}
            open={open}
            onClick={handleToggle}
            sx={slotProps?.subheader}
          >
            {subheader}
          </Subheader>

          <Collapse in={open}>{renderContent}</Collapse>
        </>
      ) : (
        renderContent
      )}
    </NavLi>
  );
}
